/**
 * Shared virtual folder-tree layout: builds the folder hierarchy encoded in an item's
 * slash-separated name ('a/b/file' — thread description or file input_file) and sorts
 * siblings according to the user's sort mode. The leaf type is generic: callers supply
 * how to read the name and ord_num and how to emit their own NodeSource via walkTree's visitor.
 */

export type SortMode = 'description' | 'ord_num' | 'description/ord_num';

export type FileLeaf<T> = { item: T; filename: string };

export type FolderNode<T> = {
	name: string;
	pathChunks: string[];
	folders: Map<string, FolderNode<T>>;
	files: FileLeaf<T>[];
	minOrd: number;
};

export type SiblingEntry<T> = { folder?: FolderNode<T>; file?: FileLeaf<T> };
export type SiblingComparator<T> = (a: SiblingEntry<T>, b: SiblingEntry<T>) => number;

const byName = (a: string, b: string) => a < b ? -1 : a > b ? 1 : 0;
// 0/unset ord_num sorts after any filled value (only relevant for the ord_num modes)
const ordWeight = (o: number) => o > 0 ? o : Infinity;

function createFolder<T>(name: string, pathChunks: string[]): FolderNode<T> {
	return { name, pathChunks, folders: new Map(), files: [], minOrd: Infinity };
}

/**
 * Builds a nested folder tree from the virtual paths in each item's name ('a/b/file').
 * Folder order in the Map follows insertion (name-asc) order; sorting happens at walk time.
 * `getOrd` returns the raw ord_num (0 when unset); items without ord_num just pass () => 0.
 */
export function buildFolderTree<T>(items: T[], getName: (t: T) => string, getOrd: (t: T) => number): FolderNode<T> {
	const root = createFolder<T>('', []);
	items.forEach(item => {
		const chunks = (getName(item) ?? '').split('/');
		const filename = chunks.pop();
		const ord = ordWeight(getOrd(item));

		let dir = root;
		if (ord < dir.minOrd) dir.minOrd = ord;
		const path: string[] = [];
		for (const segment of chunks) {
			path.push(segment);
			let child = dir.folders.get(segment);
			if (!child) {
				child = createFolder<T>(segment, [...path]);
				dir.folders.set(segment, child);
			}
			dir = child;
			if (ord < dir.minOrd) dir.minOrd = ord; // propagate the min ord_num up the whole subtree
		}

		dir.files.push({ item, filename });
	});
	return root;
}

export function makeSiblingComparator<T>(mode: SortMode, getOrd: (t: T) => number): SiblingComparator<T> {
	const entryName = (e: SiblingEntry<T>) => e.folder ? e.folder.name : e.file.filename;
	const entryOrd = (e: SiblingEntry<T>) => e.folder ? e.folder.minOrd : ordWeight(getOrd(e.file.item));

	if (mode === 'ord_num') {
		// folders and files interleave by ord_num (folder = min ord_num of its subtree)
		return (a, b) => {
			const d = entryOrd(a) - entryOrd(b); // NaN when both Infinity -> falls through to name
			if (d) return d;
			return byName(entryName(a), entryName(b));
		};
	}

	if (mode === 'description') {
		// pure alphabetical; folders before files, ord_num ignored
		return (a, b) => {
			if (!!a.folder !== !!b.folder) return a.folder ? -1 : 1;
			return byName(entryName(a), entryName(b));
		};
	}

	// 'description/ord_num' (default) - folders by name, then files by ord_num
	return (a, b) => {
		if (!!a.folder !== !!b.folder) return a.folder ? -1 : 1;
		if (a.folder) return byName(a.folder.name, b.folder.name);
		const oa = getOrd(a.file.item) || 0;
		const ob = getOrd(b.file.item) || 0;
		if (oa !== ob) return oa - ob;
		return byName(a.file.filename, b.file.filename);
	};
}

/**
 * Pre-order DFS over the folder tree. Siblings are sorted by `compare`; `visit.folder`/`visit.file`
 * emit the caller's own nodes. `level` is the depth of the current folder's direct children.
 */
export function walkTree<T>(
	folder: FolderNode<T>,
	compare: SiblingComparator<T>,
	visit: {
		folder: (folder: FolderNode<T>, level: number) => void;
		file: (leaf: FileLeaf<T>, parent: FolderNode<T>, level: number) => void;
	},
	level = 0,
): void {
	const entries: SiblingEntry<T>[] = [];
	folder.folders.forEach(f => entries.push({ folder: f }));
	folder.files.forEach(file => entries.push({ file }));
	entries.sort(compare);

	for (const entry of entries) {
		if (entry.folder) {
			visit.folder(entry.folder, level);
			walkTree(entry.folder, compare, visit, level + 1);
		} else {
			visit.file(entry.file, folder, level);
		}
	}
}
